import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
  Collapse,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CampaignIcon from "@mui/icons-material/Campaign";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import FilterListIcon from "@mui/icons-material/FilterList";
import MenuIcon from "@mui/icons-material/Menu";
import AdminPushSetup from "../Notifications/AdminPushSetup";

const MONTH_LABELS = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

function buildMonthOptions(count = 36) {
  const now = new Date();
  const options = [{ value: "all", label: "Todos os meses" }];

  for (let i = 0; i < count; i += 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    options.push({
      value: `${date.getFullYear()}-${month}`,
      label: `${MONTH_LABELS[date.getMonth()]} ${date.getFullYear()}`,
    });
  }

  return options;
}

const HeaderAdmin = ({
  currentView,
  searchQuery,
  onSearch,
  filter,
  setFilter,
  orderFilter,
  setOrderFilter,
  monthFilter = "all",
  setMonthFilter,
  onOpenDiscountCampaign,
  onOpenAccessHoldQueue,
  onOpenMenu,
  onRefresh,
  loading,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [filtersOpen, setFiltersOpen] = useState(false);
  const monthOptions = useMemo(() => buildMonthOptions(), []);

  const activeFilterCount = [
    filter !== "all",
    orderFilter !== "DESC",
    monthFilter && monthFilter !== "all",
  ].filter(Boolean).length;

  const clearFilters = () => {
    setFilter("all");
    setOrderFilter("DESC");
    setMonthFilter?.("all");
  };

  const toolbarButtonSx = {
    minHeight: 40,
    height: 40,
    px: 1.25,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.8125rem",
    flex: isMobile ? "1 1 0" : "none",
    minWidth: 0,
    boxShadow: "none",
    "&:hover": {
      transform: "none",
      boxShadow: "none",
    },
  };

  const compactSelectSx = {
    height: 36,
    fontSize: "0.8125rem",
    bgcolor: theme.palette.background.default,
    "& .MuiSelect-select": {
      py: 0.75,
      display: "flex",
      alignItems: "center",
    },
  };

  const renderCompactSelect = (label, value, onChange, children, span = false) => (
    <Box sx={{ minWidth: 0, gridColumn: span ? "1 / -1" : "auto" }}>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mb: 0.4,
          color: theme.palette.text.secondary,
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      <Select
        size="small"
        fullWidth
        value={value}
        onChange={onChange}
        sx={compactSelectSx}
      >
        {children}
      </Select>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        padding: isMobile ? "10px 12px" : "12px 20px",
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: "100%",
          minWidth: 0,
          flexWrap: "wrap",
        }}
      >
        {isMobile && onOpenMenu && currentView !== "usuarios" && (
          <IconButton
            onClick={onOpenMenu}
            aria-label="Abrir menu"
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: "10px",
              color: theme.palette.primary.main,
              bgcolor: "rgba(0, 212, 255, 0.08)",
              "&:hover": { bgcolor: "rgba(0, 212, 255, 0.16)" },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <AdminPushSetup variant="header" />
      </Box>

      {currentView === "usuarios" ? (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 0,
              width: "100%",
            }}
          >
            {isMobile && onOpenMenu && (
              <IconButton
                onClick={onOpenMenu}
                aria-label="Abrir menu"
                sx={{
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  borderRadius: "10px",
                  color: theme.palette.primary.main,
                  bgcolor: "rgba(0, 212, 255, 0.08)",
                  "&:hover": { bgcolor: "rgba(0, 212, 255, 0.16)" },
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <TextField
              variant="outlined"
              size="small"
              placeholder="Pesquisar..."
              value={searchQuery}
              onChange={(e) => onSearch(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") onSearch(e.target.value);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => onSearch(searchQuery)}
                      edge="end"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                minWidth: 0,
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  backgroundColor: theme.palette.background.default,
                },
              }}
            />

            <IconButton
              onClick={() => setFiltersOpen((open) => !open)}
              aria-label="Filtros"
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: "10px",
                border: "1px solid",
                borderColor:
                  filtersOpen || activeFilterCount > 0
                    ? "rgba(0, 212, 255, 0.55)"
                    : "rgba(255, 255, 255, 0.16)",
                bgcolor:
                  filtersOpen || activeFilterCount > 0
                    ? "rgba(0, 212, 255, 0.12)"
                    : "transparent",
                color:
                  filtersOpen || activeFilterCount > 0
                    ? theme.palette.primary.main
                    : theme.palette.text.primary,
              }}
            >
              <Badge
                color="primary"
                badgeContent={activeFilterCount}
                overlap="circular"
              >
                <FilterListIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
              minWidth: 0,
              flexWrap: "wrap",
            }}
          >
            <Button
              onClick={onOpenAccessHoldQueue}
              startIcon={<WarningAmberRoundedIcon sx={{ fontSize: 18 }} />}
              sx={{
                ...toolbarButtonSx,
                bgcolor: "rgba(255, 152, 0, 0.14)",
                color: "#ffb74d",
                border: "1px solid rgba(255, 152, 0, 0.28)",
                "&:hover": {
                  ...toolbarButtonSx["&:hover"],
                  bgcolor: "rgba(255, 152, 0, 0.22)",
                },
              }}
            >
              Reembolsos
            </Button>

            <Button
              onClick={onOpenDiscountCampaign}
              startIcon={<CampaignIcon sx={{ fontSize: 18 }} />}
              sx={{
                ...toolbarButtonSx,
                bgcolor: "rgba(255, 64, 129, 0.14)",
                color: "#ff80ab",
                border: "1px solid rgba(255, 64, 129, 0.28)",
                "&:hover": {
                  ...toolbarButtonSx["&:hover"],
                  bgcolor: "rgba(255, 64, 129, 0.22)",
                },
              }}
            >
              Campanha
            </Button>

            <IconButton
              onClick={onRefresh}
              disabled={loading}
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: "10px",
                border: "1px solid rgba(255, 255, 255, 0.16)",
                color: theme.palette.text.primary,
                "&:hover": { bgcolor: "rgba(255, 255, 255, 0.06)" },
              }}
            >
              {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Box>

          <Collapse in={filtersOpen} sx={{ width: "100%" }}>
            <Box
              sx={{
                p: 1,
                borderRadius: "10px",
                border: "1px solid rgba(255, 255, 255, 0.08)",
                bgcolor: "rgba(13, 27, 42, 0.55)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 0.75,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
                >
                  Filtros
                </Typography>
                <Button
                  size="small"
                  onClick={clearFilters}
                  disabled={activeFilterCount === 0}
                  sx={{
                    minHeight: 28,
                    px: 1,
                    textTransform: "none",
                    fontSize: "0.75rem",
                    color: theme.palette.primary.main,
                    "&:hover": { transform: "none", boxShadow: "none" },
                    "&.Mui-disabled": { color: "rgba(255,255,255,0.28)" },
                  }}
                >
                  Limpar
                </Button>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1.3fr" },
                  gap: 1,
                }}
              >
                {renderCompactSelect(
                  "Status",
                  filter,
                  (e) => setFilter(e.target.value),
                  [
                    <MenuItem key="all" value="all">Todos</MenuItem>,
                    <MenuItem key="paid" value="paid">Pagos</MenuItem>,
                    <MenuItem key="unpaid" value="unpaid">Não Pagos</MenuItem>,
                    <MenuItem key="expired" value="expired">Expirados</MenuItem>,
                    <MenuItem key="test" value="test">Teste</MenuItem>,
                  ]
                )}
                {renderCompactSelect(
                  "Ordem",
                  orderFilter,
                  (e) => setOrderFilter(e.target.value),
                  [
                    <MenuItem key="DESC" value="DESC">Mais Recentes</MenuItem>,
                    <MenuItem key="ASC" value="ASC">Mais Antigos</MenuItem>,
                  ]
                )}
                {renderCompactSelect(
                  "Mês de cadastro",
                  monthFilter || "all",
                  (e) => setMonthFilter?.(e.target.value),
                  monthOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  )),
                  isMobile
                )}
              </Box>
            </Box>
          </Collapse>
        </>
      ) : null}
    </Box>
  );
};

export default HeaderAdmin;
